// @givmo/app-charity — the console's ONE standing read, shared by everything that renders off it
// (CPA-WEB40 · shell contract v3).
//
// ══ ONE READER, MOUNTED ONCE THE SCOPE EXISTS ══════════════════════════════════════════════════
//
// The charity's standing is `GET /org/{org_ref}/me`, through the package's own single reader
// (`useOrgAccessGate` → `useOrgSelfAccess`). It is the only such answer in this app, and every
// consumer — the landing rule, the squares, the sidebar, the direct-link gate, the Admin Console's
// capability gates, the preferences page — reads the SAME query, so independently-derived answers
// to "what may this person open" cannot drift. Two projections come off it here, both the package's
// own shapes: the per-module `standings` (`standingsFromModuleRows`) and the read-level `read`
// (`standingReadFrom`: the effective permission set, the class-independent capabilities, the pending
// count, the chosen landing module).
//
// ⚠ THE BUILD LAW: PERMISSION CHECKS, NEVER ADMIN CHECKS. Nothing here exposes a tier. The host
// membership's role is class content (the class pages' Owner gates, the legacy Payments rule while
// the read is unreported) and is read where that content lives, never in the contract-owned console.
//
// ⚠ THE READ CANNOT FIRE BEFORE `activeCharityId` EXISTS, and the package hook has no `enabled`
// parameter (its `orgRef` is a required string). So the reader is a leaf component mounted only
// once the memberships read has resolved an active organization — the memberships RTT, then the
// self RTT. It is a LEAF on purpose: putting the hook in a wrapper that swaps component types when
// readiness flips would remount every page beneath it, and one of those pages (the invite redeem)
// submits a single-use token on mount.
//
// ⚠ 404 IS ABSENT, NOT ERROR. Where `ORG_CONSOLE_ENABLED` is unset the read answers the route-miss
// envelope. That is `isUnavailable` — a calm, terminal fact that maps to `standings: null` and an
// unreported `read` (the permissive posture: every mounted square a link, every declared row a
// link, NO Admin Console tile) — and it is deliberately NOT `errored`, which is the recoverable
// transport failure the direct-link gate offers a retry for.
//
// ══ THE ONE DONATIONS RULE THIS HOST KEEPS (spec §9-14) ════════════════════════════════════════
//
// A REPORTED map that lacks `donations` is a backend that predates CPA-BE28's catalog row — a
// deploy-ordering artifact, not an authorization answer: a charity can never be "not offered" its
// own §321 channel. So, ONCE, here, the class's own module is synthesized as held and on (always-on,
// never switchable) and `donations:read` is added to a REPORTED effective set, with a warning. Every
// consumer downstream — the tile, the landing, the sidebar rows, the gate — then agrees. A map that
// carries the row, or no map at all, passes through untouched.

import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  UNREPORTED_STANDING_READ,
  standingReadFrom,
  standingsFromModuleRows,
  useOrgAccessGate,
  type ModuleStandings,
  type OrgSelfAccess,
  type StandingRead,
} from '@givmo/org-console';
import { useActiveOrg } from '../org/active-org-context';
import { CLASS_DEFAULT_SLUG, DONATIONS_READ_PERMISSION } from './module-table';

/** What the one standing read is saying right now. */
export interface CharityConsoleState {
  /** The organization the read is scoped to, or `null` before one has resolved. */
  orgRef: string | null;
  /** The read has not settled (or cannot start yet because no organization has resolved). */
  pending: boolean;
  /** The read settled with a transport failure (not a 404). Recoverable; the gate offers a retry. */
  errored: boolean;
  /** The console is absent in this environment (a 404) — terminal and calm, never an error. */
  isUnavailable: boolean;
  /** Per-module standing, or `null` when nothing was reported (pending, absent, errored). */
  standings: ModuleStandings | null;
  /** The read-level half: permissions · capabilities · pending count · chosen landing module. */
  read: StandingRead;
  /** The §9-14 fall-through fired: the reported map lacked `donations` and it was synthesized. */
  donationsFellThrough: boolean;
  /** Re-run the standing read. The retry offered with an `errored` answer. */
  refetch: () => void;
}

const CharityConsoleContext = createContext<CharityConsoleState | null>(null);

let warnedOnce = false;

/** Test seam: forget the once-per-page warning so a case can assert it fires. */
export function resetDonationsFallThroughWarning(): void {
  warnedOnce = false;
}

export interface FallThroughResult {
  standings: ModuleStandings | null;
  read: StandingRead;
  fellThrough: boolean;
}

/**
 * The §9-14 fall-through, pure: a REPORTED map lacking `donations` gains the class's own module as
 * held and on, and a REPORTED effective set gains its read string. Anything else passes through.
 */
export function applyDonationsFallThrough(
  standings: ModuleStandings | null,
  read: StandingRead,
): FallThroughResult {
  if (standings === null || standings[CLASS_DEFAULT_SLUG] !== undefined) {
    return { standings, read, fellThrough: false };
  }
  if (!warnedOnce) {
    warnedOnce = true;
    console.warn(
      '[charity console] the reported org catalog carries no `donations` row; treating the class’s own module as held and on (spec §9-14).',
    );
  }
  const permissions =
    read.permissions === null
      ? null
      : read.permissions.includes(DONATIONS_READ_PERMISSION)
        ? read.permissions
        : [...read.permissions, DONATIONS_READ_PERMISSION];
  return {
    standings: {
      ...standings,
      [CLASS_DEFAULT_SLUG]: {
        known: true,
        held: true,
        orgEnabled: true,
        activationState: 'on',
        switchable: false,
      },
    },
    read: { ...read, permissions },
    fellThrough: true,
  };
}

/** What the leaf reader reports up. Primitives plus the stable `self` reference, so effects settle. */
interface SelfReadSnapshot {
  orgRef: string;
  isPending: boolean;
  tierUnknown: boolean;
  isUnavailable: boolean;
  self: OrgSelfAccess | null;
}

/**
 * The leaf that owns the read. Renders nothing; reports every settled change to the provider.
 *
 * Keyed by `orgRef` at the call site, so an org switch is a fresh reader over a fresh query key —
 * and `refetch` is handed up through a ref rather than state, because the package returns a new
 * function every render and an effect keyed on it would never settle.
 */
function SelfReadProbe({
  orgRef,
  onSnapshot,
  refetchRef,
}: {
  orgRef: string;
  onSnapshot: (snapshot: SelfReadSnapshot) => void;
  refetchRef: { current: () => void };
}) {
  const gate = useOrgAccessGate(orgRef);
  const { isPending, tierUnknown, isUnavailable, self, refetch } = gate;
  refetchRef.current = refetch;
  useEffect(() => {
    onSnapshot({ orgRef, isPending, tierUnknown, isUnavailable, self });
  }, [orgRef, isPending, tierUnknown, isUnavailable, self, onSnapshot]);
  return null;
}

const NOOP = () => undefined;

export function CharityConsoleProvider({ children }: { children: ReactNode }) {
  const { activeCharityId, isReady, membershipsState } = useActiveOrg();
  const [snapshot, setSnapshot] = useState<SelfReadSnapshot | null>(null);
  const refetchRef = useRef<() => void>(NOOP);
  const onSnapshot = useCallback((next: SelfReadSnapshot) => setSnapshot(next), []);

  const orgRef = isReady ? activeCharityId : null;
  // A snapshot for a DIFFERENT organization is stale, not an answer: the probe for the new one has
  // not reported yet, and its first render is pending anyway.
  const current = snapshot !== null && snapshot.orgRef === orgRef ? snapshot : null;

  const value = useMemo<CharityConsoleState>(() => {
    if (orgRef === null) {
      return {
        orgRef: null,
        pending: membershipsState === 'pending',
        errored: false,
        isUnavailable: false,
        standings: null,
        read: UNREPORTED_STANDING_READ,
        donationsFellThrough: false,
        refetch: NOOP,
      };
    }
    if (current === null || current.isPending) {
      return {
        orgRef,
        pending: true,
        errored: false,
        isUnavailable: false,
        standings: null,
        read: UNREPORTED_STANDING_READ,
        donationsFellThrough: false,
        refetch: () => refetchRef.current(),
      };
    }
    const reported = applyDonationsFallThrough(
      current.self === null ? null : standingsFromModuleRows(current.self.modules),
      current.self === null ? UNREPORTED_STANDING_READ : standingReadFrom(current.self),
    );
    return {
      orgRef,
      pending: false,
      errored: current.tierUnknown,
      isUnavailable: current.isUnavailable,
      standings: reported.standings,
      read: reported.read,
      donationsFellThrough: reported.fellThrough,
      refetch: () => refetchRef.current(),
    };
  }, [orgRef, current, membershipsState]);

  return (
    <>
      {orgRef !== null ? (
        <SelfReadProbe
          key={orgRef}
          orgRef={orgRef}
          onSnapshot={onSnapshot}
          refetchRef={refetchRef}
        />
      ) : null}
      <CharityConsoleContext value={value}>{children}</CharityConsoleContext>
    </>
  );
}

/**
 * Read the console's standing, or `null` outside a {@link CharityConsoleProvider} — for a class page
 * that also renders in a harness without the console, and applies its legacy rule there.
 */
export function useOptionalCharityConsole(): CharityConsoleState | null {
  return use(CharityConsoleContext);
}

/** Read the console's standing. Throws outside a {@link CharityConsoleProvider}. */
export function useCharityConsole(): CharityConsoleState {
  const context = use(CharityConsoleContext);
  if (context === null) {
    throw new Error('useCharityConsole must be used within a <CharityConsoleProvider>.');
  }
  return context;
}

export { CharityConsoleContext };
